import { useMemo } from "react";

import { useAuth } from "@/contexts/AuthContext";

/**
 * A stable per-user identifier, used as the SECOND element of every query key.
 *
 * This is the first of three layers keeping one user's cached data away from
 * the next (the other two are the `clearQueryCache()` calls on logout and on a
 * same-tab user switch). Even if a cache entry somehow survived a logout, its
 * key would not match the new user's, so it could never be served to them.
 *
 * `loggedEmail` is the anchor: it is written at login (`Login.tsx`) and removed
 * at logout (`AuthContext.logout`), which makes it the most reliable identity
 * available on the client. `userDetail` and the user's name are fallbacks for
 * any auth path that doesn't set it.
 *
 * NOTE: `CustomerSystemID` is deliberately absent — it is derived server-side
 * from the JWT and never sent by the client, so it isn't available here. Email
 * is unique per tenant-user, which is sufficient.
 */
export function useUserKey(): string {
  const { user, userDetail } = useAuth();

  return useMemo(() => {
    const email =
      typeof window !== "undefined"
        ? window.localStorage.getItem("loggedEmail")
        : null;
    return email || userDetail || user?.name || "anon";
    // `user` / `userDetail` are the re-read triggers: `setAuth` writes both, and
    // `logout` clears both, so the memo recomputes on exactly the transitions
    // that can change the identity. `loggedEmail` is written in the same event
    // handler as `setAuth`, so it is already in localStorage by the time the
    // resulting re-render runs this.
    //
    // The auth TOKEN is deliberately NOT a dependency and never part of the
    // value: it rotates on every silent refresh (~50 min), and keying the cache
    // on it would throw the whole cache away each time.
  }, [user, userDetail]);
}
